import { EventType, type MatrixClient, type Room, RoomType } from 'matrix-js-sdk'
import type { IChat } from '@/modules/chat-matrix-fast/types/IChat'
import {
  CHAT_TYPES,
  isGroupLikeChatType,
  isNonHumanDialogTitle,
  looksLikePersonName,
  pickFullerPersonTitle,
  pickRealChatTitle,
  pickTotalMembers,
} from '@/modules/chat-matrix-fast/constants/chat'
import { formatMatrixDisplayName } from '@/modules/chat-matrix-fast/services/matrixUserId'
import { toAuthenticatedMatrixMediaUrl } from '@/modules/chat-matrix-fast/services/matrixMediaUrl'
import {
  getMatrixMessagesFromRoom,
  hashToNumber,
} from '@/modules/chat-matrix-fast/services/matrixEventMapper'
import {
  lastMessageDebugLog,
  summarizeLastMessageForLog,
} from '@/modules/chat-matrix-fast/utils/chatDebugLog'
import { countMatrixRoomUnread } from '@/modules/chat-matrix-fast/utils/topics'
import { getMatrixEventRoomMeta } from '@/modules/chat-matrix-fast/services/matrixRoomMetadata'

export { getMatrixEventRoomMeta } from '@/modules/chat-matrix-fast/services/matrixRoomMetadata'

const PLACEHOLDER_ROOM_NAMES = new Set(['empty room', 'empty_room', 'empty'])

function isPlaceholderRoomName(name: string): boolean {
  const normalized = name.trim().toLowerCase()
  return !normalized || PLACEHOLDER_ROOM_NAMES.has(normalized)
}

function hasExplicitRoomName(room: Room): boolean {
  const nameEvent = room.currentState?.getStateEvents(EventType.RoomName, '')
  const stateName = nameEvent?.getContent?.()?.name?.trim()

  return Boolean(
    stateName
    && !isPlaceholderRoomName(stateName)
    && stateName !== room.roomId,
  )
}

/** m.direct account data — каноничный признак DM, в т.ч. с m.room.name = ФИО собеседника. */
function isListedInDirectAccountData(room: Room): boolean {
  try {
    const event = room.client?.getAccountData?.(EventType.Direct)
    const content = event?.getContent?.() as Record<string, unknown> | undefined
    if (!content || typeof content !== 'object') {
      return false
    }

    return Object.values(content).some(
      roomIds => Array.isArray(roomIds) && roomIds.includes(room.roomId),
    )
  } catch {
    return false
  }
}

/** Имя комнаты совпадает с display name участника — типичный Element/DM паттерн. */
function roomNameMatchesJoinedMember(room: Room): boolean {
  const roomName = (room.name || '').trim()
  if (!roomName || isPlaceholderRoomName(roomName)) {
    return false
  }

  return (room.getJoinedMembers() ?? []).some(member => {
    const name = (member.name || member.rawDisplayName || '').trim()
    return Boolean(name) && name === roomName
  })
}

function isMatrixSpaceRoom(room: Room): boolean {
  return room.isSpaceRoom?.() === true || room.getType?.() === RoomType.Space
}

function getActiveSpaceStateEvents(room: Room, eventType: EventType.SpaceChild | EventType.SpaceParent) {
  const events = room.currentState?.getStateEvents(eventType) ?? []
  const list = Array.isArray(events) ? events : [events]

  return list.filter(event => {
    if (!event?.getStateKey?.()) {
      return false
    }

    return Object.keys(event.getContent?.() ?? {}).length > 0
  })
}

function getParentSpaceId(room: Room): string | undefined {
  const parents = getActiveSpaceStateEvents(room, EventType.SpaceParent)
  const canonical = parents.find(event => event.getContent()?.canonical === true)

  return (canonical ?? parents[0])?.getStateKey() || undefined
}

function getSpaceChildRoomIds(room: Room): string[] {
  return getActiveSpaceStateEvents(room, EventType.SpaceChild)
    .map(event => event.getStateKey())
    .filter((roomId): roomId is string => Boolean(roomId))
}

export function isMatrixDirectRoom(room: Room): boolean {
  if (isMatrixSpaceRoom(room)) {
    return false
  }

  const createEvent = room.currentState?.getStateEvents(EventType.RoomCreate, '')
  const createContent = createEvent?.getContent?.()

  if (createContent?.is_direct === true) {
    return true
  }

  if (room.getDMInviter?.()) {
    return true
  }

  if (isListedInDirectAccountData(room)) {
    return true
  }

  const memberCount = room.getJoinedMemberCount()

  // DM часто получают m.room.name = ФИО пира; не считаем их группой из‑за имени.
  if (hasExplicitRoomName(room)) {
    if (memberCount <= 2 && roomNameMatchesJoinedMember(room)) {
      return true
    }
    return false
  }

  if (room.getMxcAvatarUrl?.()) {
    return false
  }

  return memberCount <= 2
}

export function isFallbackDialogTitle(chat: IChat): boolean {
  if (isNonHumanDialogTitle(
    chat.title,
    chat.matrix_room_id,
    chat.matrix_peer_id ?? chat.dialog_member?.matrix_user_id,
  )) {
    return true
  }

  if (chat.type === 'dialog' || chat.type === 'direct') {
    return !looksLikePersonName(chat.title)
  }

  return false
}

export function preserveGroupChatMetadata(previous: IChat, fetched: IChat): IChat {
  const wasGroup = isGroupLikeChatType(previous.type) || previous.type === 'space'
  const fetchedAsDialog = fetched.type === 'dialog' || fetched.type === 'direct'
  const fetchedTitle = pickRealChatTitle(fetched)
  const previousTitle = pickRealChatTitle(previous)
  const titledFetched = fetchedTitle && fetchedTitle !== fetched.title
    ? { ...fetched, title: fetchedTitle }
    : fetched

  if (!wasGroup) {
    const withMember = {
      ...titledFetched,
      dialog_member: titledFetched.dialog_member ?? previous.dialog_member,
    }
    // Keep known peer display name — Matrix may fall back to mxid/phone on reload.
    if (
      fetchedAsDialog
      && previousTitle
      && !isFallbackDialogTitle(previous)
      && isFallbackDialogTitle(withMember)
    ) {
      return { ...withMember, title: previousTitle }
    }

    if (fetchedAsDialog) {
      const fuller = pickFullerPersonTitle(withMember, previous)
      if (fuller && fuller !== withMember.title) {
        return { ...withMember, title: fuller }
      }
    }

    return withMember
  }

  // Spaces больше не first-class UX — не поднимаем type: space из Matrix
  if (previous.type === 'space' || fetched.type === 'space') {
    return titledFetched
  }

  if (fetchedAsDialog) {
    // Backend dialog (dialog_member) побеждает Matrix-only group:
    // DM с m.room.name=ФИО раньше попадали в store как group, и merge
    // навсегда залипал на type/title группы при смене вкладки.
    if (fetched.dialog_member) {
      const withMember = {
        ...titledFetched,
        dialog_member: titledFetched.dialog_member ?? previous.dialog_member,
      }
      return {
        ...withMember,
        title: pickFullerPersonTitle(withMember, previous) || fetchedTitle || previousTitle,
      }
    }

    return {
      ...titledFetched,
      type: previous.type,
      title: previousTitle || fetchedTitle,
      description: previous.description ?? fetched.description,
      icon: fetched.icon || previous.icon,
      discussion_mode: previous.discussion_mode ?? fetched.discussion_mode,
      topics: previous.topics ?? fetched.topics,
      capabilities: previous.capabilities ?? fetched.capabilities,
      space_room_id: previous.space_room_id ?? fetched.space_room_id,
    }
  }

  return {
    ...titledFetched,
    title: fetchedTitle || previousTitle,
    description: fetched.description ?? previous.description,
    icon: fetched.icon || previous.icon,
    discussion_mode: fetched.discussion_mode ?? previous.discussion_mode,
    topics: fetched.topics?.length ? fetched.topics : (previous.topics ?? fetched.topics),
    capabilities: fetched.capabilities ?? previous.capabilities,
    space_room_id: fetched.space_room_id ?? previous.space_room_id,
    topics_display_mode: fetched.topics_display_mode ?? previous.topics_display_mode,
    total_members: pickTotalMembers(fetched.total_members, previous.total_members),
    // Backend/group chat must not inherit Matrix space-parent (sidebar hide bug)
    parent_space_id: previous.parent_space_id ?? fetched.parent_space_id,
  }
}

export function getMatrixPeerId(room: Room, myUserId: string | null | undefined): string | undefined {
  if (!isMatrixDirectRoom(room) || !myUserId) {
    return undefined
  }

  const members = room.currentState?.getMembers() ?? room.getJoinedMembers()
  return members
    .map(member => member.userId)
    .find(userId => userId !== myUserId)
}

function resolveDialogRoomTitle(room: Room, matrixPeerId?: string): string {
  if (matrixPeerId) {
    const member = room.getMember(matrixPeerId)
    const memberName = (member?.name || member?.rawDisplayName || '').trim()
    if (memberName && !memberName.startsWith('@') && !isPlaceholderRoomName(memberName)) {
      return memberName
    }

    return formatMatrixDisplayName(matrixPeerId)
  }

  const roomName = (room.name || '').trim()
  if (roomName && !isPlaceholderRoomName(roomName) && roomName !== room.roomId) {
    return roomName
  }

  return roomName || room.roomId
}

function resolveRoomIconUrl(
  room: Room,
  matrixClient?: MatrixClient | null,
  myUserId?: string | null,
): string | null {
  if (!matrixClient) {
    return null
  }

  if (isMatrixDirectRoom(room) && myUserId) {
    const peerId = getMatrixPeerId(room, myUserId)
    const peerMxcAvatar = peerId ? room.getMember(peerId)?.getMxcAvatarUrl?.() : null
    if (peerMxcAvatar) {
      return toAuthenticatedMatrixMediaUrl(matrixClient, peerMxcAvatar, 96, 96, 'crop')
    }
  }

  const mxcAvatar = room.getMxcAvatarUrl?.()
  if (!mxcAvatar) {
    return null
  }

  return toAuthenticatedMatrixMediaUrl(matrixClient, mxcAvatar, 96, 96, 'crop')
}

export function roomToChat(room: Room, myUserId?: string | null, matrixClient?: MatrixClient | null): IChat {
  const roomMessages = getMatrixMessagesFromRoom(room)
  const lastMessage = roomMessages[roomMessages.length - 1]
  const timelineEvents = room.getLiveTimeline().getEvents().length

  const eventMeta = getMatrixEventRoomMeta(room)
  const isSpace = isMatrixSpaceRoom(room)
  const isEvent = !isSpace && Boolean(eventMeta?.event_url || eventMeta?.chat_id)
  const isDialog = !isSpace && !isEvent && isMatrixDirectRoom(room)
  const matrixPeerId = isDialog ? getMatrixPeerId(room, myUserId) : undefined
  const roomName = (room.name || '').trim()
  const title = isDialog
    ? resolveDialogRoomTitle(room, matrixPeerId)
    : isEvent
      ? (roomName && !isPlaceholderRoomName(roomName) ? roomName : '')
      : (roomName && !isPlaceholderRoomName(roomName) ? roomName : room.roomId)

  if (lastMessage) {
    lastMessageDebugLog('roomToChat · matrix timeline last_message', {
      roomId: room.roomId,
      title,
      timelineEvents,
      messageCount: roomMessages.length,
      last_message: summarizeLastMessageForLog(
        lastMessage as Parameters<typeof summarizeLastMessageForLog>[0],
      ),
    })
  }

  const type = isSpace
    ? CHAT_TYPES.SPACE
    : isEvent
      ? CHAT_TYPES.EVENT
      : isDialog
        ? CHAT_TYPES.DIALOG
        : CHAT_TYPES.GROUP

  return {
    id: eventMeta?.chat_id ?? hashToNumber(room.roomId),
    matrix_room_id: room.roomId,
    matrix_peer_id: matrixPeerId,
    type,
    title,
    description: '',
    icon: resolveRoomIconUrl(room, matrixClient, myUserId),
    owner: room.getCreator?.() || '',
    members: [],
    created_at: new Date(room.getLastActiveTimestamp() || Date.now()).toISOString(),
    unread_count: isSpace ? 0 : countMatrixRoomUnread(room.roomId),
    last_message: isSpace ? undefined : (lastMessage ?? undefined),
    last_message_id: isSpace ? undefined : lastMessage?.id,
    total_members: room.getJoinedMemberCount(),
    event_url: isSpace ? null : (eventMeta?.event_url ?? null),
    parent_space_id: isSpace ? undefined : getParentSpaceId(room),
    child_room_ids: isSpace ? getSpaceChildRoomIds(room) : undefined,
  }
}
